import { useTranslation } from "react-i18next";
import {
	ChartCard,
	ColumnChart,
	Donut,
	HBarList,
	holatVar,
	Legend,
	Lollipop,
} from "@/shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import {
	BolimlarRoyxati,
	HaftaSoatIssiqlik,
	kategoriyaRangi,
	kunlikUstunlar,
	type Qatlam,
	segmentliQatorlar,
	TOP_N,
} from "./shared";

/**
 * Lidlar analitikasi (reja E3).
 *
 * Tartib: KPI → [kunlar (onlayn/oflayn) | formalar donut'i] →
 * [hafta kuni × soat | hududlar] → [xodimlar lollipop | bo'limlar].
 *
 * ⚠️ `flagged` (oflayn sinxron) — `primary` ning QISMI; onlayn =
 * `primary − flagged` (`shared.tsx`).
 * ⚠️ `breakdown` kaliti — forma SARLAVHASI (erkin matn), enum EMAS:
 * `t()` dan O'TKAZILMAYDI — sarlavha tasodifan i18n kalitiga o'xshasa
 * boshqa matn chiqib qolardi.
 */

const RANG = {
	onlayn: holatVar("kelgan"),
	oflayn: holatVar("kechikkan"),
} as const;

export function LeadsAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bosh = t("analytics.generic.empty");
	const onlaynNom = t("analytics.leads.online");
	const oflaynNom = t("analytics.leads.offline");
	const qatlam: Qatlam = {
		asosiy: { label: onlaynNom, color: RANG.onlayn },
		belgilangan: { label: oflaynNom, color: RANG.oflayn },
	};
	const qiymatMatni = (total: string, flagged: string) =>
		t("analytics.leads.value", { total, flagged });
	const legenda = (
		<Legend
			items={[
				{ label: onlaynNom, color: RANG.onlayn },
				{ label: oflaynNom, color: RANG.oflayn },
			]}
		/>
	);

	const jami = data.totals.find((x) => x.key === "leads")?.value ?? 0;
	const ustunlar = kunlikUstunlar(data.daily, qatlam, (date, base, flagged) =>
		t("analytics.leads.dayTooltip", { date, base, flagged })
	);

	return (
		<div data-testid="leads-analytics" className="flex flex-col gap-3.5">
			<KpiRow kind={kind} totals={data.totals} spark={data.spark} />

			<div className="grid gap-3.5 lg:grid-cols-[1.6fr_1fr]">
				<div data-widget="daily">
					<ChartCard
						title={t("analytics.leads.dailyTitle")}
						subtitle={t("analytics.leads.dailySubtitle")}
						extra={legenda}
					>
						<ColumnChart columns={ustunlar} height={200} empty={bosh} />
					</ChartCard>
				</div>

				<div data-widget="forms">
					<ChartCard title={t("analytics.leads.formsTitle")}>
						<Donut
							segments={data.breakdown.map((b, i) => ({
								key: b.key,
								value: b.count,
								color: kategoriyaRangi(i),
								label: b.key,
							}))}
							centerValue={sonMatni(jami)}
							centerLabel={t("analytics.leads.formsCenter")}
							formatValue={sonMatni}
							empty={bosh}
						/>
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-[1.4fr_1fr]">
				<div data-widget="heat">
					<ChartCard
						title={t("analytics.events.heatTitle")}
						subtitle={t("analytics.leads.heatSubtitle")}
					>
						<HaftaSoatIssiqlik heat={data.heat} />
					</ChartCard>
				</div>

				<div data-widget="zones">
					<ChartCard
						title={t("analytics.leads.zonesTitle")}
						subtitle={t("analytics.leads.zonesSubtitle")}
						extra={legenda}
					>
						<HBarList
							empty={bosh}
							rows={segmentliQatorlar(
								data.byZone,
								qatlam,
								(z) => z.zoneId,
								(z) => z.zoneName,
								qiymatMatni
							)}
						/>
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-2">
				<div data-widget="top-employees">
					<ChartCard
						title={t("analytics.leads.topTitle")}
						extra={
							<Legend
								items={[
									{ label: t("analytics.leads.total"), color: RANG.onlayn, shape: "dot" },
									{ label: oflaynNom, color: RANG.oflayn, shape: "dot" },
								]}
							/>
						}
					>
						<Lollipop
							rows={data.topEmployees.slice(0, TOP_N).map((x) => ({
								key: x.employeeId,
								label: x.employeeName,
								total: x.total,
								flagged: x.flagged,
							}))}
							totalColor={RANG.onlayn}
							flaggedColor={RANG.oflayn}
							totalLabel={t("analytics.leads.total")}
							flaggedLabel={oflaynNom}
							empty={bosh}
						/>
					</ChartCard>
				</div>

				<div data-widget="departments">
					<ChartCard title={t("analytics.events.departmentsTitle")} extra={legenda}>
						<BolimlarRoyxati rows={data.byDepartment} qatlam={qatlam} qiymatMatni={qiymatMatni} />
					</ChartCard>
				</div>
			</div>
		</div>
	);
}
